// Note: This file is required by the 11ty build. It is crucial that you do not
// add third party libraries here like moment to keep that build small.

const constants = {
	// When a user logs in and checks remember me, we remember them for this many
	// days, or longer until they stop being active.
	REMEMBER_SESSION_DAYS: 4 * 7,
	// When a user logs in via SSO token, we remember them for this many days, or
	// longer until they stop being active.
	SSO_SESSION_DAYS: 4 * 7,
	// If they don't check remember me, we remember them for  this many hours, or
	// longer until they stop being active.
	NORMAL_SESSION_HOURS: 2,
	// If a user is active in a session that is about to expire, keep increasing
	// it by this number of minutes
	SESSION_INCREASE_INTERVAL_MINUTES: 30,
	// If a user navigates to a url, then doesn't log in within this number of
	// minutes, the request for that page expires and they will be directed to
	// home upon login.
	REQUEST_PAGE_EXPIRE_MINUTES: 2,
	// If a user fully completes a set, how many days to pass before they are
	// allowed to three star that set (rather than just having 1 available).
	MAX_STARS_RETRY_LIMIT_DAYS: 1,
	// If a user fails a test, how many days to pass before they are allowed
	// to take the test again.
	TEST_FAIL_COOLDOWN_DAYS: 1,
	// If a user quits a test, how many days to pass before they are allowed
	// to take the test again.
	TEST_QUIT_COOLDOWN_DAYS: 1,
	// Registration, reset password, PO password setting, parent registration code lengths
	REGISTRATION_CODE_LENGTH: 7,
	PASSWORD_RESET_CODE_LENGTH: 7,
	PO_PASSWORD_SET_CODE_LENGTH: 7,
	PARENT_REGISTRATION_CODE_LENGTH: 7,
	ACCOUNT_SETUP_CODE_LENGTH: 14,
	UNSUBSCRIBE_CODE_LENGTH: 14,
	// Time user is on a library page for it to be count as read,
	BOOK_PAGE_READ_SECONDS: 1,
	// Position in video (between 0 and 1) for it to count as watched.
	VIDEO_POSITION_WATCHED: 0.75,
	// Earliest trainer activity showable on activity calendar
	EARLIEST_TRAINER_ACTIVITY_DATE: "2016-08-01",
	// Minimum minutes doing BA content to continue student streak
	MIN_CONTENT_TIME_FOR_STREAK: 0.1,
	// Maximum quantity of any cart item
	ITEM_LIMIT: 99,
	// Maximum number of active subscription items allowed in a cart/parent
	MAX_SUBSCRIPTIONS_FOR_PARENT: 6,
	// Maximum number of subscription items (active + inactive) allowed in a parent
	MAX_INACTIVE_SUBSCRIPTIONS_FOR_PARENT: 10,
	// Max student display name length
	MAX_DISPLAY_NAME_LENGTH: 15,
	// Valid choices for pencil tool line width
	PENCIL_LINE_WIDTHS: [2, 4, 6],
	// 0 = Kindergarten (for rostering/display only; no K curriculum)
	ALLOWED_CLASS_GRADES: [0, 1, 2, 3, 4, 5, 6, 7, 8],
	ALLOWED_BA_LEVELS: Object.freeze([1, 2, 3, 4, 5]), // hasn't replaced every reference
	// Default value for the pencil tool line width
	DEFAULT_PENCIL_LINE_WIDTH: 4,
	// Default value for sound volume
	DEFAULT_SOUNDS_VOLUME: 50,
	// Default volume for videos in the theater (between 0 and 1)
	DEFAULT_THEATER_VOLUME: 1,
	// Default playback speed for videos in the theater (between 0.5 and 2)
	DEFAULT_THEATER_PLAYBACK_RATE: 1,
	// When the privacy policy was last updated in YYYY-MM-DD form
	PRIVACY_LAST_UPDATED: "2023-08-31",
	// ErrorUtil types to skip logging for now
	LOW_PRIORITY_ERRORS: [
		"E_BANVAS_VIEW_RECT",
		"E_BATEX_DRAW",
		"E_CHAIN_LAYOUT_NAN",
		"E_CANVASMATH",
		"E_MEASURE_COMP_LOOP",
		"E_UNSTARTED_SUBMIT",
		"E_NO_AVATAR",
	],
	// ErrorUtil types to send us an email for.
	CRITICAL_FRONTEND_ERRORS: ["E_REACT_CRASH"],
	// ErrorUtil types that don't get logged until happening twice. Values of
	// this object are number of milliseconds within which the second occurrence
	// must occur. 0 means no limit.
	REQUIRE_REPEAT_ERRORS: {
		E_BUNDLE_FAILED: 7200000,
	},
	// Whether to use the older logFrontendErrors endpoint
	USE_OLD_SERVER_ERROR_LOGGING: true,
	// Whether to use Sentry for logging errors
	USE_SENTRY: false,
	// Maximum number of errors to log in one frontend page load
	DEFAULT_ERROR_LIMIT: 10,
	// The smallest chapter in a grade we lock automatically.
	SMALLEST_ACADEMY_CHAPTER_TO_LOCK: 4,
	// For AoPS Academy contest
	POINTS_PER_STAR: 500,
	POINTS_PER_TROPHY: 800,

	BA_DEMO_EMAIL_SIGN_UP_OUTREACH_SOURCE_ID: 5,

	// Need to update version requirements table if any of these changes
	RECOMMENDED_BROWSERS: ["chrome", "firefox", "safari", "edge-chromium"],
	ALLOWED_BROWSERS: [
		"chrome",
		"firefox",
		"safari",
		"edge-chromium",
		"silk",
		"edge-ios",
	],
	ALLOWED_MOBILE_BROWSERS: ["ios", "crios", "fxios"],
	// For apparel sizes
	APPAREL_CLASSES: ["youth", "mens", "womens", "adult"],
	// wanted to remove "womens" from previous line but it gave an error when I did.
	// I'm not sure it's necessary to remove, though, so I'll leave it in for now.
	APPAREL_SIZES: [
		"x-small",
		"small",
		"medium",
		"large",
		"x-large",
		"2x-large",
		"3x-large",
	],
	// Minimum password lengths
	MINIMUM_PASSWORD_LENGTH: 5,
	MINIMUM_EMAIL_PASSWORD_LENGTH: 8,

	ACADEMY_PURCHASE_ID: 2,
	BEAM_PURCHASE_ID: 109,
	LUMEN_SCHOOL_ID: 600,
	VIRTUAL_CAMPUS_SCHOOL_ID: 328,
	TEST_CAMPUS_ID: 22,

	// Minimum delay (milliseconds) to refresh after detecting that user is on
	// an outdated version of client code.
	VERSION_CHECK_MIN_DELAY: 10000,
	// Maximum delay (milliseconds) to refresh after detecting that user is on
	// an outdated version of client code.
	VERSION_CHECK_MAX_DELAY: 60000,

	TUTORIAL_BLOCK_BUCKS: 10,
	TUTORIAL_BLOCK_XP: 20,

	// How many years a bulk code lasts before it expires
	BULK_CODE_LIFETIME_IN_YEARS: 2,

	// Book test submission requirements.
	BOOK_TEST_PART_1_MIN_MINUTES: 20,
	BOOK_TEST_PART_2_MIN_MINUTES: 30,

	// Went-live events
	// The date the BA redesign went live
	REDESIGN_WENT_LIVE: "2020-11-09",
	// The date the parent leaderboards went live
	PARENT_LEADERBOARDS_WENT_LIVE: "2021-03-09",
	// The date chapter path went live
	CHAPTER_PATH_WENT_LIVE: "2022-07-25",
	// Launch of Platform broke teacher welcome modal. This is the date
	// the fix for it went live. Platform went live on 2022-11-07.
	TEACHER_WELCOME_FIX_WENT_LIVE: "2022-12-01",

	MIN_TEACHERS: 2,
	// Seconds to ban a guest when rate limited.
	UNAUTH_TOO_MANY_REQUESTS_BAN_LENGTH: 600,
	// Seconds to ban a logged in user when rate limited.
	AUTH_TOO_MANY_REQUESTS_BAN_LENGTH: 10,
	// Leaderboards gets the top N people in a category.
	// This is N.
	LEADERBOARDS_LENGTH: 50,
	// People with this rank or below can be winners in Academy contest
	ACADEMY_LEADERBOARDS_WINNER_RANK_CUTOFF: 10,
	// Max number of beasts students can save in beast gallery modal
	// (not including profile avatar)
	MAX_BEASTS_IN_GALLERY: 5,

	// XP level at which the puzzle lab is unlocked
	PUZZLE_LAB_XP_LEVEL_UNLOCK: 10,

	// the default grade to use to calculate bundle prices - currently only used for upgrades
	DEFAULT_BUNDLE_GRADE: 3,

	// Maximum number of subscriptions a family account can have
	MAX_SUBS: 6,

	// When ENABLE_NEW_CONTACT_US is on, /contact navigations redirect here (see main.js, Router.js).
	NEW_CONTACT_US_EXTERNAL_URL:
		"https://artofproblemsolving.com/contact?product=ba",

	// "See what's next" CTA for Beast Academy graduates (AcademyGraduateModal).
	WHATS_NEXT_AFTER_BEAST_ACADEMY_ARTICLE_URL:
		"https://artofproblemsolving.com/blog/articles/whats-next-after-beast-academy",
};

module.exports = constants;
